import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, Check, Copy, Mail, MapPin, Phone, User } from "lucide-react";
import { toast } from "sonner";
import { useConversationContactPacket } from "@/hooks/conversation/useConversationContactPacket";
import {
  formatPacketAddress,
  formatPacketLocation,
  formatPacketPhone,
  resolveContactPacketStatus,
} from "@/lib/contact/mobileContactPacket";
import { extractCustomer, type ExtractedCustomer } from "@/lib/conversation/customer";
import type { MobileContactPacket } from "@/types/contact/mobileContactPacket";
import { Conversation } from "@/types/conversation/conversation";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ConversationCustomerCardProps {
  conversation: Conversation;
}

/**
 * Right-rail "Contact details" card — one of the three things a thread has to
 * show at a glance, alongside the conversation and its summary. Sits at the
 * top of the rail rather than in a footer.
 *
 * Two sources, kept visibly apart because they are not equally trustworthy:
 *
 * 1. The contact packet the caller shared through the Help Genie app, joined
 *    on their number (HG-1296). Stated by the person themselves.
 * 2. Whatever the genie picked out of this one call. Inferred from speech.
 *
 * The empty state is the common case until app-side collection ships, so it
 * says what is missing and why — never a skeleton that implies a load which
 * never finishes, and never a placeholder that looks like real data.
 *
 * Owner-facing, HG brand chrome per guidelines/design.md §6 — never tenant
 * primaryColor.
 */
export function ConversationCustomerCard({ conversation }: ConversationCustomerCardProps) {
  const callCaptured = useMemo(() => extractCustomer(conversation), [conversation]);
  const { data, isLoading, isError } = useConversationContactPacket(conversation);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const packet = data?.packet ?? null;
  const callerE164 = data?.callerE164 ?? null;

  const status = resolveContactPacketStatus({ isLoading, isError, callerE164, packet });

  const packetFields = useMemo(() => buildPacketFields(packet), [packet]);
  const callFields = useMemo(() => buildCallFields(callCaptured), [callCaptured]);
  const hasAnything = packetFields.length > 0 || callFields.length > 0;

  const handleCopy = async (key: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      toast.success("Copied");
      setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
    } catch {
      toast.error("Couldn't copy. Select manually.");
    }
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 py-4 border-b border-[#4E9CFF]/10"
          style={{
            background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)",
          }}
        >
          <div className="flex items-center gap-3">
            <GenieIcon
              name="user"
              size={24}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="Contact details"
            />
            <h3 className="text-base font-bold text-gray-900">Contact details</h3>
          </div>
        </div>

        <div className="px-5 py-5 space-y-5">
          {packetFields.length > 0 && (
            <section>
              <SectionLabel>Shared in the app</SectionLabel>
              <div className="space-y-3">
                {packetFields.map((field) => (
                  <Field
                    key={field.key}
                    icon={field.icon}
                    label={field.label}
                    value={field.value}
                    onCopy={() => handleCopy(field.key, field.value)}
                    copied={copiedKey === field.key}
                  />
                ))}
              </div>
              {packet?.collectedAt && (
                <p className="mt-3 text-[11px] text-gray-500">
                  Last updated by the caller {formatDate(packet.collectedAt)}
                </p>
              )}
            </section>
          )}

          {status === "loading" && packetFields.length === 0 && (
            <p className="text-sm text-gray-500">Checking for shared contact details…</p>
          )}

          {status === "error" && (
            <p className="text-sm text-gray-600 leading-relaxed">
              Couldn't check for shared contact details just now. Anything captured on the
              call is still shown below.
            </p>
          )}

          {callFields.length > 0 && (
            <section className={packetFields.length > 0 ? "pt-4 border-t border-gray-100" : ""}>
              <SectionLabel>Captured on this call</SectionLabel>
              <div className="space-y-3">
                {callFields.map((field) => (
                  <Field
                    key={field.key}
                    icon={field.icon}
                    label={field.label}
                    value={field.value}
                    onCopy={() => handleCopy(field.key, field.value)}
                    copied={copiedKey === field.key}
                  />
                ))}
              </div>
            </section>
          )}

          {!hasAnything && status !== "loading" && (
            <div className="text-sm text-gray-600 leading-relaxed">
              <p>
                {status === "no-caller"
                  ? "No contact details yet. This conversation has no caller number, so details shared in the app can't be matched to it."
                  : "No contact details yet. They appear here once this caller shares them in the Help Genie app."}
              </p>
              <Link
                to={`/genie-hub/my-genies/${conversation.agent_id}?section=section-insights`}
                className="block mt-3 text-xs"
              >
                <span className="bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent font-semibold">
                  Add capture fields to insights →
                </span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold mb-3">
      {children}
    </div>
  );
}

interface FieldProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
}

function Field({ icon, label, value, onCopy, copied }: FieldProps) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="flex-shrink-0 mt-0.5 text-gray-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
        <div className="text-sm font-medium text-gray-900 break-words">{value}</div>
      </div>
      <button
        type="button"
        onClick={onCopy}
        className="flex-shrink-0 p-1.5 rounded-md hover:bg-gradient-to-r hover:from-[#4E9CFF]/10 hover:to-[#FF4DB8]/10 transition-colors text-gray-400 hover:text-[#4E9CFF]"
        aria-label={`Copy ${label}`}
      >
        {copied ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <Copy className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

interface DisplayField {
  key: string;
  label: string;
  value: string;
  icon: React.ReactNode;
}

type FieldCandidate = Omit<DisplayField, "value"> & { value: string | null };

/** Packet fields in the order an owner reads them: who, where, how to reach. */
function buildPacketFields(packet: MobileContactPacket | null): DisplayField[] {
  if (!packet) return [];

  return keepPopulated([
    { key: "packet-name", label: "Name", value: packet.name, icon: <User className="h-4 w-4" /> },
    {
      key: "packet-location",
      label: "Location",
      value: formatPacketLocation(packet.location),
      icon: <MapPin className="h-4 w-4" />,
    },
    {
      key: "packet-address",
      label: "Address",
      value: formatPacketAddress(packet.address),
      icon: <MapPin className="h-4 w-4" />,
    },
    {
      key: "packet-phone",
      label: "Phone",
      value: formatPacketPhone(packet.phone),
      icon: <Phone className="h-4 w-4" />,
    },
    { key: "packet-email", label: "Email", value: packet.email, icon: <Mail className="h-4 w-4" /> },
    {
      key: "packet-company",
      label: "Company",
      value: packet.companyName,
      icon: <Briefcase className="h-4 w-4" />,
    },
    { key: "packet-role", label: "Role", value: packet.jobTitle, icon: <Briefcase className="h-4 w-4" /> },
  ]);
}

function buildCallFields(customer: ExtractedCustomer): DisplayField[] {
  return keepPopulated([
    { key: "call-name", label: "Name", value: customer.name, icon: <User className="h-4 w-4" /> },
    { key: "call-email", label: "Email", value: customer.email, icon: <Mail className="h-4 w-4" /> },
    { key: "call-phone", label: "Phone", value: customer.phone, icon: <Phone className="h-4 w-4" /> },
  ]);
}

/** Drops every field with no value — an empty field is never rendered blank. */
function keepPopulated(candidates: FieldCandidate[]): DisplayField[] {
  return candidates
    .filter((candidate): candidate is DisplayField => Boolean(candidate.value))
    .map(({ key, label, value, icon }) => ({ key, label, value, icon }));
}

function formatDate(value: string): string {
  try {
    return new Date(value).toLocaleDateString([], { dateStyle: "medium" });
  } catch {
    return value;
  }
}
